import "./index.css";

import { Field, Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { useAtom } from "jotai";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useParams } from "react-router";

import { useAddressBookAccessorials } from "~/features/quote/hooks/use-address-book-accessorials.ts";
import { useQuoteValidation } from "~/features/quote/hooks/use-quote-validation.ts";
import { useTemplateModals } from "~/features/quote/hooks/use-template-modals.ts";
import { AddEditTemplateModal } from "~/features/templates/components/add-edit-template-modal/add-edit-template-modal.tsx";
import { TemplatesModal } from "~/features/templates/components/templates-modal/templates-modal.tsx";
import { FormDatePicker } from "~/shared/components/fields/form-date-picker.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import {
	getMinShipDate,
	isHoliday,
	isPastShipDate,
	isWeekend,
	parseRobustDate,
} from "~/shared/utils/date-helpers.ts";

import { useQuoteAddressContext } from "../../context/quote-address-context.tsx";
import { QuoteDetailsContext } from "../../context/quote-details-context.tsx";
import {
	useErrorHandler,
	useFilteredHolidays,
	useFormHandlers,
	useGetCustomerDetails,
	useQuoteDetailsPageLoadQueries,
	useQuoteLoader,
	useUserDefaultsFetcher,
} from "../../hooks/use-quote-details-hooks.ts";
import { useQuoteDetailsLoading } from "../../hooks/use-quote-details-loading.ts";
import { scrollToFirstError } from "../../utils/scroll-to-first-error.ts";
import { AdditionalDetails } from "./components/additional-details/additional-details.tsx";
import { CommodityToolbar } from "./components/commodity-toolbar/commodity-toolbar.tsx";
import { CustomerSelectRow } from "./components/customer-select-row/customer-select-row.tsx";
import { PickupDeliveryDetails } from "./components/pickup-delivery-details/pickup-delivery-details.tsx";
import { QuoteCommoditiesWrapper } from "./components/quote-commodities-wrapper/quote-commodities-wrapper.tsx";

type QuoteDetailsFormProps = {
	onStepChange: () => void;
	onSubmit?: () => Promise<{ success: boolean; error?: string }>;
	submitButtonText?: string;
	isSubmitting?: boolean;
	templateMode?: boolean;
	disableSubmitButton?: boolean;
	forceExternal?: boolean;
	simulateMode?: boolean;
};

export const QuoteDetailsForm = ({
	onStepChange,
	onSubmit,
	submitButtonText,
	isSubmitting,
	templateMode = false,
	disableSubmitButton = false,
	forceExternal = false,
	simulateMode = false,
}: QuoteDetailsFormProps) => {
	const { isReady: isTemplatesReady, isEnabled: isTemplatesEnabled } =
		useFeatureFlagGate("traxLtlTemplatesPage");
	const isMobile = useIsMobile();

	const resolvedShowSubmitButton = !templateMode;
	const resolvedSkipUserDefaults = templateMode;
	const { isInternal: isInternalDeployment } = useDeploymentType();
	const isInternal = !forceExternal && isInternalDeployment;
	const minShipDate = useMemo(() => getMinShipDate(isInternal), [isInternal]);
	// const data = useIsAuthenticated();
	const { quoteId } = useParams<{ quoteId: string }>();

	const { quoteState, setCustomerDetails } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { allAccessorials } = useAddressBookAccessorials();
	const [selectedCustomer] = useAtom(selectedCustomerAtom);
	const [customerCleared, setCustomerCleared] = useState(false);

	const {
		validationErrors,
		validate,
		clearFieldError,
		clearItemFieldError,
		resetValidation,
		hasFieldError,
	} = useQuoteValidation();

	// Determine if we should fetch quote data and perform related operations
	const shouldPerformQuoteOperations = useMemo(
		() => Boolean(quoteId),
		[quoteId],
	);

	// Use custom hooks for better separation of concerns
	const { shouldFetchQuote, isLoadingQuote, quoteIsFetched, isCopyNavigation } =
		useQuoteLoader({
			includeCustomerData: false, // Customer data is handled by useCustomerDetailsFetcher to avoid conflicts
			includeFormReset:
				!shouldPerformQuoteOperations && !templateMode && !simulateMode, // Do not reset when rendered inside template or simulate modal
		});
	const { showErrorModal, errorMessage, handleError, closeErrorModal } =
		useErrorHandler();
	const { getChangeHandler, formInitialValues } = useFormHandlers();
	const customerId = quoteState?.customer?.id ?? 0;

	const { data: pageData, isFetching: pageDataIsFetching } =
		useQuoteDetailsPageLoadQueries();

	const pickupCountryCode = useMemo(() => {
		return (
			quoteState.pickupCountry?.abbreviation ||
			quoteState.pickupPostalCode?.city?.state?.countryCode
		);
	}, [quoteState.pickupCountry, quoteState.pickupPostalCode]);

	const filteredHolidays = useFilteredHolidays(
		pageData?.holidays,
		pickupCountryCode,
	);

	const shipmentDateValidator = useCallback(
		(value: string | Date) => {
			if (!value) return "Shipment Date is required.";

			const dateObj = parseRobustDate(value);
			if (!dateObj) return "Invalid date format.";

			if (isPastShipDate(dateObj, isInternal))
				return "Shipment Date cannot be in the past.";

			if (isWeekend(dateObj)) return "Shipment Date cannot be a weekend.";

			if (isHoliday(dateObj, filteredHolidays))
				return "Shipment Date cannot be a holiday.";

			return "";
		},
		[filteredHolidays, isInternal],
	);

	// Use custom hook for loading logic
	const { isLoading } = useQuoteDetailsLoading(
		quoteId,
		quoteState,
		shouldPerformQuoteOperations,
		pageDataIsFetching,
		shouldFetchQuote,
		isLoadingQuote,
		quoteIsFetched,
	);

	// Automatically manage global spinner for page data loading
	useAutoSpinner(isLoading);

	const { isLoading: customerDetailsLoading } =
		useGetCustomerDetails(customerId);

	// Fetch user defaults when customer ID changes (skip if disabled or if this
	// is a Copy Quote/Shipment navigation, since user defaults would overwrite
	// the copied shipment's data with the customer's saved defaults).
	const { reapplyDefaults } = useUserDefaultsFetcher(
		quoteState.customer?.id,
		resolvedSkipUserDefaults || isCopyNavigation,
	) as { reapplyDefaults: () => void };

	useEffect(() => {
		if (!isInternal && selectedCustomer) {
			setCustomerDetails(selectedCustomer);
		}
	}, [isInternal, setCustomerDetails, selectedCustomer]);

	const isParentLoading = customerDetailsLoading || isLoadingQuote;
	const showSaveTemplateButton =
		resolvedShowSubmitButton &&
		isTemplatesReady &&
		isTemplatesEnabled &&
		!simulateMode;
	const disableSaveTemplateButton = !quoteState?.customer?.id;

	const {
		showTemplatesModal,
		toggleTemplatesModal,
		handleTemplateSelect,
		showSaveTemplateModal,
		saveTemplateQuoteSnapshot,
		saveTemplateAddressSnapshot,
		handleOpenSaveTemplateModal,
		handleCloseSaveTemplateModal,
		handleApplySavedTemplate,
		handleClearTemplate,
	} = useTemplateModals({ pageData, allAccessorials, reapplyDefaults });

	// After a failed Get Quote attempt, scroll/focus the first invalid field so
	// the error is visible without the user having to hunt for it (mobile in particular).
	const justFailedSubmitRef = useRef(false);

	const handleBeforeSubmit = useCallback((): boolean => {
		if (!validate(quoteState, isInternal, selectedAddressBookItems)) {
			justFailedSubmitRef.current = true;
			return false;
		}
		resetValidation();
		return true;
	}, [
		validate,
		quoteState,
		isInternal,
		selectedAddressBookItems,
		resetValidation,
	]);

	useEffect(() => {
		if (!justFailedSubmitRef.current) {
			return;
		}
		justFailedSubmitRef.current = false;
		scrollToFirstError(
			validationErrors,
			quoteState.items.map((item) => item.id),
		);
	}, [validationErrors, quoteState.items]);

	return (
		<>
			<Form
				key={quoteState.id || "new-quote"} // Force form re-mount when quote changes
				initialValues={formInitialValues}
				render={() => {
					return (
						<div className={"quote-details-wrapper"}>
							<FormElement>
								<fieldset className={"k-form-fieldset"}>
									<CustomerSelectRow
										customerCleared={customerCleared}
										forceExternal={forceExternal}
										getChangeHandler={getChangeHandler}
										isParentLoading={isParentLoading}
										onClearTemplate={handleClearTemplate}
										onClearValidation={() => clearFieldError("customer")}
										onToggleTemplatesModal={toggleTemplatesModal}
										setCustomerCleared={setCustomerCleared}
										showValidationError={hasFieldError("customer")}
										simulateMode={simulateMode}
										templateMode={templateMode}
									/>
									<div className={"ship-date-row"}>
										<div className={"ship-date-wrapper"}>
											<div hidden={templateMode}>
												<Field
													component={FormDatePicker}
													disabledDates={filteredHolidays}
													id={"shipmentDate"}
													label={
														<>
															Ship Date{" "}
															<span className={"required"}>&nbsp;*</span>
														</>
													}
													min={minShipDate}
													name={"shipmentDate"}
													onChange={getChangeHandler("shipmentDate")}
													required={true}
													validator={shipmentDateValidator}
													value={quoteState.shipmentDate}
													wrapperStyle={{
														marginTop: "0px",
														width: isMobile ? "100%" : "125px",
													}}
												/>
											</div>
										</div>
									</div>
									<div
										className={"quote-details-row"}
										id={"pickup-delivery-container"}
									>
										<div
											key={"pickup"}
											className={"pickup-delivery-details"}
											id={`pickup-details`}
										>
											<PickupDeliveryDetails
												name={"pickup"}
												onClearValidation={() => clearFieldError("pickup")}
												showValidationError={hasFieldError("pickup")}
											/>
										</div>
										<div
											key={"delivery"}
											className={"pickup-delivery-details"}
											id={"delivery-details"}
										>
											<PickupDeliveryDetails
												name={"delivery"}
												onClearValidation={() => clearFieldError("delivery")}
												showValidationError={hasFieldError("delivery")}
											/>
										</div>
									</div>
									<hr className={"tql-divider"} />
									<div className={"items-container"}>
										<CommodityToolbar />
										<QuoteCommoditiesWrapper
											itemValidationErrors={validationErrors.items}
											onClearItemFieldError={clearItemFieldError}
										/>
									</div>
									<AdditionalDetails
										disableSaveTemplateButton={disableSaveTemplateButton}
										disableSubmitButton={disableSubmitButton}
										isSubmitting={isSubmitting}
										onBeforeSubmit={handleBeforeSubmit}
										onError={handleError}
										onSaveTemplate={handleOpenSaveTemplateModal}
										onStepChange={onStepChange}
										onSubmit={onSubmit}
										showSaveTemplateButton={showSaveTemplateButton}
										showSubmitButton={resolvedShowSubmitButton}
										submitButtonText={submitButtonText}
									/>
								</fieldset>
							</FormElement>
						</div>
					);
				}}
			/>
			{showSaveTemplateModal && saveTemplateQuoteSnapshot ? (
				<AddEditTemplateModal
					autoApplyOnSave={false}
					initialQuoteState={saveTemplateQuoteSnapshot}
					initialSelectedAddressBookItems={saveTemplateAddressSnapshot}
					onApplyTemplate={handleApplySavedTemplate}
					toggleModal={handleCloseSaveTemplateModal}
				/>
			) : null}
			{showTemplatesModal ? (
				<TemplatesModal
					customerId={quoteState.customer?.id}
					onClose={toggleTemplatesModal}
					onSelect={handleTemplateSelect}
				/>
			) : null}
			{showErrorModal ? (
				<TraxModal
					onClose={closeErrorModal}
					title={"Quote Submission Failed"}
					type={"error"}
					width={500}
				>
					<p>{errorMessage}</p>
				</TraxModal>
			) : null}
		</>
	);
};
